import React, { PropsWithChildren } from "react";

import { TEMPORARY_isNextAuthLocalStorage } from "@/lib/localstorages";

import { useAuthContext } from "./Auth.context";

const AuthRendererContext = React.createContext<null | true>(null);

const useAuthRendererContext = () => {
  const context = React.useContext(AuthRendererContext);

  if (!context) {
    throw new Error(
      "AuthRenderer.IsAuth and AuthRenderer.IsUnauth should be used inside <AuthRenderer> component" +
        "for example: <AuthRenderer><AuthRenderer.IsAuth>...</AuthRenderer.IsAuth></AuthRenderer>",
    );
  }

  return context;
};

type AuthRendererType = React.FC<PropTypes> & {
  IsAuth: React.FC<PropsWithChildren>;
  IsUnauth: React.FC<PropsWithChildren>;
};

type PropTypes = {
  children: React.ReactNode;
};

export const AuthRenderer: AuthRendererType = ({ children }: PropTypes) => {
  const value = true;

  return (
    <AuthRendererContext.Provider value={value}>
      {children}
    </AuthRendererContext.Provider>
  );
};

const IsAuth: React.FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  useAuthRendererContext();
  const isNextAuth = TEMPORARY_isNextAuthLocalStorage.get();

  return isNextAuth && isAuthenticated ? <>{children}</> : null;
};

const IsUnauth: React.FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  useAuthRendererContext();
  const isNextAuth = TEMPORARY_isNextAuthLocalStorage.get();

  return isNextAuth && isAuthenticated ? null : <>{children}</>;
};

AuthRenderer.IsAuth = React.memo(IsAuth);
AuthRenderer.IsUnauth = React.memo(IsUnauth);
